import { useMemo } from 'react';
import {
  CareProgressBadgeType,
  PMMDocument,
  PMMMedProfile,
  type CareProgressRowData,
} from '../../../../Table/components/CareProgress/types';
import { Form, Stack } from 'react-bootstrap';
import { convertToFormattedDateString } from '../../../../../helpers';
import { CheckCircle } from 'react-bootstrap-icons';
import { HourGlass } from '../../../../Icons';
import { useMedicationProfilesShared } from '../../../context/MedicationProfilesProvider';

interface PMMDocumentFieldProps {
  id: string;
  name: string;
  data: CareProgressRowData[];
}

const PMMDocumentField = ({ id, name, data = [] }: PMMDocumentFieldProps) => {
  if (!data) {
    data = [];
  }
  const { medProfiles } = useMedicationProfilesShared();
  const sortedData = useMemo(
    () =>
      [...data]
        .sort(
          (a, b) =>
            new Date(b.event_date).getTime() - new Date(a.event_date).getTime(),
        )
        .filter(
          (item) => item.detail.status !== CareProgressBadgeType.NO_DOCUMENT,
        ),
    [data],
  );

  const latestInProgressData = useMemo(
    () =>
      sortedData.find(
        (item) => item.detail.status === CareProgressBadgeType.IN_PROGRESS,
      ) ?? null,
    [sortedData],
  );

  const latestData = latestInProgressData ?? sortedData[0] ?? null;

  const getBadge = () => {
    if (!latestData)
      return (
        <span className="pmm-not-started mb-0 pdf-font-size-25">
          Not Started
        </span>
      );
    switch (latestData.detail.status) {
      case CareProgressBadgeType.COMPLETED:
        return (
          <span className="pmm-completed mb-0 pdf-font-size-25">
            Completed
            <CheckCircle color="#43AC8B" size={14} />
          </span>
        );
      case CareProgressBadgeType.IN_PROGRESS:
        return (
          <span className="pmm-progress mb-0 pdf-font-size-25">
            In progress
            <HourGlass color="#E0B36A" size={14} />
          </span>
        );
      case CareProgressBadgeType.NO_DOCUMENT:
        return (
          <span className="pmm-not-started pdf-font-size-25">Not Started</span>
        );
    }
  };

  const completedProfiles = useMemo(() => {
    if (name != PMMMedProfile.MED_PROFILE) {
      return [];
    }
    const filtered = medProfiles.filter(
      (item) => item.profileStatus === 'finalised',
    );
    return filtered;
  }, [medProfiles, name]);

  const completedCount = useMemo(() => {
    const completedFromData = sortedData.filter(
      (item) => item.detail.status === CareProgressBadgeType.COMPLETED,
    ).length;

    if (name === PMMMedProfile.MED_PROFILE) {
      return completedProfiles.length;
    }

    return completedFromData;
  }, [completedProfiles.length, name, sortedData]);

  const additionalCompletedCount = useMemo(() => {
    if (!latestData) return 0;

    if (latestData.detail.status === CareProgressBadgeType.COMPLETED) {
      return Math.max(0, completedCount - 1);
    }

    return completedCount;
  }, [completedCount, latestData]);

  return (
    <>
      <Stack
        direction="horizontal"
        className={
          'labelRow my-3 justify-content-between align-items-center height-36'
        }
      >
        <Form.Label className="footer-label mb-0">{name}</Form.Label>
        <div className="d-flex flex-column width-90">
          <Form.Label className="footer-value mb-0 pdf-font-size-25">
            {getBadge()}
          </Form.Label>
          {additionalCompletedCount > 0 ? (
            <>
              <span
                className="pmm-sub-badge pdf-font-size-25"
                data-tip
                data-for={id}
              >{`+ ${additionalCompletedCount} completed`}</span>
            </>
          ) : latestData ? (
            <div className="pmm-sub-badge pdf-font-size-25" />
          ) : (
            <></>
          )}
        </div>
        {latestData ? (
          <span className="pmm-date pdf-font-size-25">
            {latestData.name == PMMDocument.REVIEW && <span>Last review:</span>}
            {convertToFormattedDateString(latestData.event_date)}
          </span>
        ) : (
          <span className="pmm-date pdf-font-size-25" />
        )}
      </Stack>
    </>
  );
};

export default PMMDocumentField;
